import React, { useCallback, useEffect, useState } from 'react';
import './Tables.scss';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import Paper from '@mui/material/Paper';
import { Grid } from 'src/shared/grid/Grid/Grid';
import { SiteMetaData } from '../../field-sample/Sites/SiteMetaData';
import CustomTablePagination from '../../../shared/TablePagination/CustomTablePagination';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Autocomplete, TextField } from '@mui/material';
import moment from 'moment';
import { RoundDecimal2, errorResponse, isValidDate } from 'src/store/common';
import { useStoreContext } from 'src/store/context';
import { useAddAlert } from 'src/store/actions';
import { getSamples, getSiteMetaData } from 'src/apis/getFuelSampleCalls';
import LoadingSpinner from 'src/shared/loading/loading';
import { GridContainer } from 'src/shared/grid';
import { alertMessages } from 'src/constants';

type FuelSampleProps = {
  fuelSampleProps: any
};

/**
 * This will show the station meta data content of the accordion 
 * after user clicks on station meta data
 * 
 */

export const FuelSampleTable: React.FC<FuelSampleProps> = ({ fuelSampleProps }) => {
  const [state, dispatch] = useStoreContext();
  const addAlert = useAddAlert(dispatch);

  const [subCategorySelection, setSubCategorySelection] = useState('All');
  const [methodSelection, setMethodSelection] = useState('All');

  const [errorStartDateMsg, setErrorStartDateMsg] = useState<string|null>(null);
  const [errorEndDateMsg, setErrorEndDateMsg] = useState<string|null>(null);

  const [allSamps, setAllSamps]=useState<any[]|null>(null);
  const [siteMetaData, setSiteMetaData] = useState<any>();
  const [totalCount, setTotalCount] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [samplesCallComplete, setSamplesCallComplete] = useState<boolean>(true);
  const fuelSampleMetaDataInput = {
    startDate: null,
    endDate: null,
    fuelType: "All",
    fuelSubCategory: "All",
    fuelMethod: "All",
    fuelCategory: "All",
  }
  const fuelSampleResponse = useCallback((request: any) =>{
    (async()=>{
      try{
        const returnFromPromise:any = await getSamples(addAlert, setSamplesCallComplete, request);
        const samples= returnFromPromise.samplesForHeader;
        const pageInfo = returnFromPromise.pageInfo
        setTotalCount(pageInfo.total_count);
        setSamplesCallComplete(true);
        if(samples){
          samples.forEach((sample:any)=>{
            sample.formatted_date = moment.utc(sample.sample).utcOffset(state.userTimeZone.offset).format('MM/DD/YYYY');
          });
          setAllSamps(samples);
        }
        else{
          setAllSamps([{
            id:0
          }])
        }
      }
      catch(error){
        setSamplesCallComplete(true);
        errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);

        setAllSamps([])
        console.log('error', error)
      } 
    })();
  },[addAlert, state.userTimeZone.offset]);

    /* when the show table button is pressed/true then this should render the table*/
  useEffect(() => {
    if(fuelSampleProps.isShowTableData)  {
      const fuel={
        startDate: fuelSampleProps.startDate,
        endDate: fuelSampleProps.endDate,
        fuel_type: fuelSampleProps.sampleType,
        status: 'All',
        category: "All",
        sub_category: subCategorySelection,
        method: methodSelection,
        siteId: fuelSampleProps.siteInfo?.site_id,
        page: page,
        rowsPerPage: rowsPerPage,

      }
      fuelSampleResponse(fuel);
    }
  }, [fuelSampleProps.sampleType, subCategorySelection, methodSelection, fuelSampleProps.startDate, fuelSampleProps.siteInfo,
    fuelSampleProps.endDate, page, rowsPerPage, fuelSampleProps.isShowTableData, fuelSampleResponse]);

  interface FieldSampleData {
    dateSample: string,
    avgValue: string,
    type: string,
    subCategory: string,
    method: string
  }

  interface HeadCell {
    disablePadding: boolean;
    id: keyof FieldSampleData;
    label: string;
    numeric: boolean;
  }

  const headCells: readonly HeadCell[] = [
    {
      id: 'dateSample',
      numeric: false,
      disablePadding: true,
      label: 'DATE',
    },
    {
      id: 'avgValue',
      numeric: false,
      disablePadding: false,
      label: 'SAMPLE AVG VALUE',
    },
    {
      id: 'type',
      numeric: false,
      disablePadding: false,
      label: 'TYPE',
    },
    {
      id: 'subCategory',
      numeric: false,
      disablePadding: false,
      label: 'SUB-CATEGORY',
    },
    {
      id: 'method',
      numeric: false,
      disablePadding: false,
      label: 'METHOD',
    },
  
  ];

  function EnhancedTableHead() {
    return (
      <TableHead>
        <TableRow>
          {headCells.map((headCell) => (
            <TableCell
              className='headerBasicFieldTable'
              key={headCell.id}
              align={headCell.numeric ? 'right' : 'left'}
              style={{ minWidth: '278px', height: '50px', fontSize: '20px', width: '100px' }}
              tabIndex={0}
            >
              {headCell.label}
            </TableCell>

          ))}
        </TableRow>
      </TableHead>
    );
  }  

  const handleSiteMetadata = () => {
    fuelSampleProps.setOpenMetaData(!fuelSampleProps.openSiteMetaData);
    if(fuelSampleProps.openSiteMetaData !== true) {
      (async()=>{
        try{
            const siteMeta:any = await getSiteMetaData(null, addAlert,fuelSampleProps.siteInfo.site_id, fuelSampleMetaDataInput);
            setSiteMetaData(siteMeta[0])
          }
        catch(error){
          errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
        } 
      })();
    }
  }
  const handleStartDate = (newValue: any)=>{
    if( !isValidDate(newValue) ){
      setErrorStartDateMsg('Invalid Date');
      return;
    }
    fuelSampleProps.setStartDate(moment(newValue).utc().toDate());
    const startDateChange={
      startDate: moment(newValue).utc().toDate(),
      endDate: fuelSampleProps.endDate,
      fuel_type: fuelSampleProps.sampleType,
      status: 'All',
      category: 'All',
      sub_category: subCategorySelection,
      method: methodSelection,
      siteId: fuelSampleProps.siteInfo.site_id,
      page: page,
      rowsPerPage: rowsPerPage,

    }
    fuelSampleResponse(startDateChange);
    if((moment(newValue) <= moment(fuelSampleProps.endDate))){
      if(errorEndDateMsg=== 'Invalid Date' || (moment()<=moment(fuelSampleProps.startDate)) || moment()<=moment(fuelSampleProps.endDate) || newValue=== 'Invalid Date'){
        return
      }
      
      else{
        if(errorEndDateMsg!=='Invalid Date'){
          setErrorEndDateMsg(null)
        }
        setErrorStartDateMsg(null)
      }
    }
    else if(moment(newValue) > moment(fuelSampleProps.endDate)) {
      setErrorStartDateMsg('Start Date is after End Date');
    }
    else{
      setErrorStartDateMsg('Invalid Date');
    }
  }
  const handleEndDate = (newValue: any)=>{
    if( !isValidDate(newValue) ){
      setErrorEndDateMsg('Invalid Date');
      return;
    }
    fuelSampleProps.setEndDate(moment(newValue).utc().toDate());
    const endDateChange={
      startDate: fuelSampleProps.startDate,
      endDate: moment(newValue).utc().toDate(),
      fuel_type: fuelSampleProps.sampleType,
      status: 'All',
      category: "All",
      sub_category: subCategorySelection,
      method: methodSelection,
      siteId: fuelSampleProps.siteInfo.site_id,
      page: page,
      rowsPerPage: rowsPerPage,

    }
    fuelSampleResponse(endDateChange);

    if((moment(newValue) >= moment(fuelSampleProps.startDate))){
      if(errorStartDateMsg=== 'Invalid Date' || (moment()<=moment(fuelSampleProps.startDate)) || (moment()<=moment(fuelSampleProps.endDate)) || newValue=== 'Invalid Date'){
        return
      }
      else{
        if(errorStartDateMsg!=='Invalid Date'){
          setErrorStartDateMsg(null)
        }
        setErrorEndDateMsg(null)
      }
    }
    else if(moment(newValue) < moment(fuelSampleProps.startDate)){
      setErrorEndDateMsg('End Date is before Start Date');
    }
    else{
      setErrorEndDateMsg('Invalid Date');
    }
  }
  
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    const fuelNewPage={
      startDate: fuelSampleProps.startDate,
      endDate: fuelSampleProps.endDate,
      fuel_type: fuelSampleProps.sampleType,
      status: 'All',
      category: "All",
      sub_category: subCategorySelection,
      method: methodSelection,
      siteId: fuelSampleProps.siteInfo.site_id,
      page: newPage,
      rowsPerPage: rowsPerPage,

    }
    fuelSampleResponse(fuelNewPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
    const fuelPerPg={
      startDate: fuelSampleProps.startDate,
      endDate: fuelSampleProps.endDate,
      fuel_type: fuelSampleProps.sampleType,
      status: 'All',
      category: 'All',
      sub_category: subCategorySelection,
      method: methodSelection,
      siteId: fuelSampleProps.siteInfo.site_id,
      page: 0,
      rowsPerPage: Number.parseInt(event.target.value, 10),

    }
    fuelSampleResponse(fuelPerPg);
  }

  //  *** START MAIN RETURN ***
  return(
    <div>
    {fuelSampleProps.showRequiredFieldsLegend &&
      <Grid row gap="md" className="margin-top-3" desktop={{ col: 12 }}>
        <Grid>
          <span className="font-sans-md text-bold">*Select required fields above</span>
        </Grid>
      </Grid>
    }
    {
      !fuelSampleProps.showRequiredFieldsLegend && 
      <Grid row gap="md" className="margin-top-3" desktop={{ col: 12 }}>
        <colgroup>
          <col width="15%" />
          <col width="10%" />
          <col width="75%" />
        </colgroup>
        <tbody>
          <tr>
            <td style={{color: 'black', fontSize: 'x-large'}}>{fuelSampleProps.siteInfo.site_name}</td>
            <td style={{fontSize: 'medium'}}>
              <button className="usa-accordion__button button-meta-data"
                type='button'
                data-testid={'open-site-meta-data-button'}
                aria-expanded={fuelSampleProps.openSiteMetaData}
                onClick={handleSiteMetadata}>SITE METADATA</button>
            </td>
          </tr>
        </tbody>
        {fuelSampleProps.openSiteMetaData && siteMetaData && <SiteMetaData fromFieldSample={false} site={siteMetaData}/>}
      </Grid>
    }
      
    {/* Second set of input fields (Calendars, Sub-Category, Method) */}
    <div id="dataTable" className={`margin-top-5 dropdown-style ${fuelSampleProps.isContentGrayedOut ? 'gray-out-content' : ''}`}>
      <Grid row gap="md" >
        <Grid desktop={{ col: 2 }}>
          <label htmlFor='sample-start-date' className="font-sans-md text-bold">Sample Start Date*</label>
        </Grid>
        <Grid desktop={{ col: 2 }}>
          <label htmlFor='sample-end-date' className="font-sans-md text-bold">Sample End Date</label>
        </Grid>
        <Grid desktop={{ col: 2 }}>
          <label htmlFor='sub-category-selection' className="font-sans-md text-bold">Sub-Category</label>
        </Grid>
        <Grid desktop={{ col: 2 }}>
          <label htmlFor='method-selection' className="font-sans-md text-bold">Method</label>
        </Grid>
      </Grid>
      <Grid row gap="md" className="margin-top-1">

        {/*** Calendar Widget: *** SAMPLE START DATE  ****/}
        <Grid desktop={{ col: 2 }}>
          <DatePicker
            disableFuture
            value={fuelSampleProps.startDate}
           
            slotProps={{
              textField: {
                id: "sample-start-date",
                error: (errorStartDateMsg) ? true : false,
                helperText: errorStartDateMsg
              },
            }}
            onError={(reason)=>{
              if(reason === 'disableFuture'){
                setErrorStartDateMsg('Start Date is in the Future');
              }
              else if(reason ==='invalidDate'){
                setErrorStartDateMsg('Invalid Date');
              }
              else if(reason === null && moment(fuelSampleProps.startDate) <= moment(fuelSampleProps.endDate)){
                if(errorEndDateMsg!=='Invalid Date'){
                  setErrorEndDateMsg(null)
                }
                setErrorStartDateMsg(null);
              }
              else{
                if(moment(fuelSampleProps.startDate) > moment(fuelSampleProps.endDate)){
                  setErrorStartDateMsg('Start Date is after End Date');
                }
                else{
                  setErrorStartDateMsg('An Error on Start Date')
                }
              }            
            }}
            onChange={(newStartDateValue) => { 
              handleStartDate(newStartDateValue);
            }}
            />

        </Grid>

        {/*** Calendar Widget: ** SAMPLE END DATE ****/}
        <Grid desktop={{ col: 2 }}>
          <DatePicker
            disableFuture
            value={fuelSampleProps.endDate}
            slotProps={{
              textField: {
                id: "sample-end-date",
                error: (errorEndDateMsg) ? true : false,
                helperText: errorEndDateMsg
              },
            }}
            onError={(reason)=>{
              if(reason ==='disableFuture'){
                setErrorEndDateMsg('End Date is in the Future');
              }
              else if(reason ==='invalidDate'){
                setErrorEndDateMsg('Invalid Date');
              }
              else if(reason === null && moment(fuelSampleProps.startDate) <= moment(fuelSampleProps.endDate)){
                if(errorStartDateMsg!=='Invalid Date'){
                  setErrorStartDateMsg(null)
                }                
                setErrorEndDateMsg(null);
              }
              else{
                if(moment(fuelSampleProps.endDate) < moment(fuelSampleProps.startDate)){
                  setErrorEndDateMsg('End Date is before Start Date');
                }else{
                  setErrorEndDateMsg('An Error on End Date')

                }
            }}}
            onChange={(newEndDateValue) => { 
              handleEndDate(newEndDateValue);
            }}
          />
        </Grid>

        {/***** INPUT-FIELD: Sub-Category *****/}
        <Grid desktop={{ col: 2 }}>
          <Autocomplete
            freeSolo
            forcePopupIcon={true}
            className='height-55'
            disableClearable
            value={subCategorySelection}
            onChange={(_event: any, newValue: any) => {
              setSubCategorySelection(newValue);
              const subChange={
                startDate: fuelSampleProps.startDate,
                endDate: fuelSampleProps.endDate,
                fuel_type: fuelSampleProps.sampleType,
                status: 'All',
                category: 'All',
                sub_category: newValue,
                method: methodSelection,
                siteId: fuelSampleProps.siteInfo.site_id,
                page: page,
                rowsPerPage: rowsPerPage,
          
              }
              fuelSampleResponse(subChange);
            }}
            inputValue={subCategorySelection}
            onInputChange={(_event, newInputValue) => {
              setSubCategorySelection(newInputValue);
            }}

            id="sub-category-selection"
            options={fuelSampleProps.sampleSubCats.map((option) => option.code_key)}
            renderInput={(params) => (
              <TextField
                placeholder={fuelSampleProps.methods[0].code_key}
                data-testid={'sub-category-search'}
                {...params}
                label=""
                InputProps={{
                  ...params.InputProps,
                  type: 'search',
                }}
              />
            )}
          />
        </Grid>

        {/***** INPUT-FIELD: Method *****/}
        <Grid desktop={{ col: 2 }}>
          <Autocomplete
            freeSolo
            forcePopupIcon={true}
            className='height-55'
            disableClearable
            value={methodSelection}
            onChange={(_event: any, newValue: any) => {
              setMethodSelection(newValue);
              const methodChange={
                startDate: fuelSampleProps.startDate,
                endDate: fuelSampleProps.endDate,
                fuel_type: fuelSampleProps.sampleType,
                status: 'All',
                category: 'All',
                sub_category: subCategorySelection,
                method: newValue,
                siteId: fuelSampleProps.siteInfo.site_id,
                page: page,
                rowsPerPage: rowsPerPage,
          
              }
              fuelSampleResponse(methodChange);
            }}
            inputValue={methodSelection}
            onInputChange={(_event, newInputValue) => {
              setMethodSelection(newInputValue);
            }}
            id="method-selection"
            options={fuelSampleProps.methods.map((option) => option.code_key)}
            renderInput={(params) => (
              <TextField
                placeholder={fuelSampleProps.methods[0].code_key}
                data-testid={'methods-search'}
                {...params}
                label=""
                InputProps={{
                  ...params.InputProps,
                  type: 'search',
                }}
              />
            )}
          />
        </Grid>
      </Grid>


      <Grid row gap="md" className="margin-top-2"></Grid>

      {/***** DATA TABLE   ***********/}
      <TableContainer component={Paper} className='margin-top-2 first-table MuiPaper-root-special'>
        <Table aria-label="Groups" size="small">
          <colgroup>
            <col width="10%" />
            <col width="10%" />
            <col width="10%" />
            <col width="10%" />
            <col width="10%" />
          </colgroup>
          <EnhancedTableHead/> 
          <TableBody>
            {
              samplesCallComplete? (
                fuelSampleProps.isShowTableData && allSamps !== null &&
                allSamps.map((row) => (
                  <TableRow key ={row.sample_id}>
                  <TableCell tabIndex={0} align={'center'} className={'basicFieldSampleCell'}>{row?.formatted_date}</TableCell>
                  <TableCell tabIndex={0} align={'center'} className={'basicFieldSampleCell'}>{RoundDecimal2((row?.sample_average_value))}%</TableCell>
                  <TableCell tabIndex={0} align={'center'} className={'basicFieldSampleCell'}>{row?.fuel?.fuel_type}</TableCell>
                  <TableCell tabIndex={0} align={'center'} className={'basicFieldSampleCell'}>{row?.sub_category}</TableCell>
                  <TableCell tabIndex={0} align={'center'} className={'basicFieldSampleCell'}>{row?.method_type}</TableCell>
                </TableRow>))
              ):(
                <div className='heightImportant'>
                <LoadingSpinner/>
              </div>
              )
            }
          </TableBody>
        </Table>
          {((!allSamps || ( allSamps && allSamps.length<1)) && fuelSampleProps.isShowTableData)
            && <GridContainer className="removePadding">
              <Grid row gap="md" className='page-heading'>
                <Grid
                  tabIndex={0}
                  desktop={{ col: 12 }}
                  style={{paddingTop: '2.5%', marginLeft: '1%', marginBottom: '2.5%'}}
                  className="display-flex flex-row flex-align-center flex-justify"
                >    
                  <span className="font-sans-sm text-bold">There is no data entered for the filter parameters you have selected. Try changing the filter or main search criteria.</span>
                </Grid>
              </Grid>
            </GridContainer>
          }
        <CustomTablePagination
          rowsPerPageOptions={[10, 20, 30]}
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />   
      </ TableContainer>
      
    </div>
    </div>
  );
};